import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp } from 'lucide-react';

const BACKEND_URL = '';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = new Date(label + 'T00:00:00');
  return (
    <div className="bg-white border border-slate-200 shadow-lg rounded-lg px-4 py-2.5">
      <p className="text-xs text-slate-500 font-medium">{d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
      <p className="text-sm font-bold text-emerald-700">{payload[0].value} lead{payload[0].value !== 1 ? 's' : ''}</p>
    </div>
  );
};

const LeadTrendChart = ({ token }) => {
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTrends = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/leads/trends?days=30`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const data = await res.json();
        setTrend(data.trend || []);
      }
    } catch {
      /* silently fail — chart is non-critical */
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTrends(); }, [fetchTrends]);

  const totalLeads = trend.reduce((sum, d) => sum + d.count, 0);

  const formatXAxis = (val) => {
    const d = new Date(val + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <Card className="shadow-lg mb-8" data-testid="lead-trend-chart-card">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900" data-testid="trend-chart-title">Lead Trends</h3>
              <p className="text-sm text-slate-500">Last 30 days</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-emerald-700" data-testid="trend-total">{totalLeads}</p>
            <p className="text-xs text-slate-500">total leads</p>
          </div>
        </div>

        {loading ? (
          <div className="h-64 flex items-center justify-center text-slate-400">Loading chart...</div>
        ) : trend.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-slate-400" data-testid="trend-no-data">No data available</div>
        ) : (
          <div className="h-64" data-testid="trend-chart-area">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="leadGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="date"
                  tickFormatter={formatXAxis}
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={40}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11, fill: '#94a3b8' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#059669"
                  strokeWidth={2}
                  fill="url(#leadGradient)"
                  dot={false}
                  activeDot={{ r: 5, fill: '#059669', stroke: '#fff', strokeWidth: 2 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LeadTrendChart;
